import React, { useState, useEffect, useRef } from "react";
import { Loader2, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useForgotPassword } from "../../hooks/queries/useAuthMutations";

const COOLDOWN_SECONDS = 600;

const ForgotPasswordPage = ({ onBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  const submitting = useRef(false);
  const forgotPassword = useForgotPassword();
  const isLoading = forgotPassword.isPending;

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || cooldown > 0) return;
    setError("");

    if (!email) {
      setError("El correo es obligatorio.");
      return;
    }
    if (cooldown > 0) return;

    submitting.current = true;
    try {
      // The endpoint always responds 200 (no email enumeration); the backend
      // emails a link to APP_URL/reset-password?token=...
      await forgotPassword.mutateAsync({ email });
      setSent(true);
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      if (err?.code === "rate_limited") {
        setError(
          "Has enviado demasiadas solicitudes. Espera un momento antes de intentar de nuevo.",
        );
        setCooldown(COOLDOWN_SECONDS);
      } else if (err?.code === "API_UNREACHABLE") {
        setError("Error de conexión. Inténtalo de nuevo.");
      } else {
        setError(err?.message || "Error de conexión. Inténtalo de nuevo.");
      }
    } finally { submitting.current = false; }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {!sent ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Title */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <Mail size={28} className="text-blue-950" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-wide mb-2">
                    Recuperar contraseña
                  </h1>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Ingresa tu correo electrónico para solicitar un enlace de
                    recuperación.
                  </p>
                </div>

                {/* Error */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="email"
                    placeholder="Correo electrónico*"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                    required
                  />

                  <div className="flex justify-center pt-2">
                    <button
                      type="submit"
                      disabled={isLoading || cooldown > 0}
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          Enviar enlace
                          <span className="text-base">✦</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Back */}
                <button
                  onClick={onBackToLogin}
                  className="w-full min-h-11 py-3 flex items-center justify-center gap-2 mt-6 text-sm text-gray-500 hover:text-blue-900 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Volver a iniciar sesión
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Success State */}
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={28} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide mb-2">
                    Solicitud recibida
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-2">
                    Si hay una cuenta activa con este correo, recibirás un enlace de recuperación:
                  </p>
                  <p className="text-gray-900 font-semibold text-sm mb-6">
                    {email}
                  </p>
                  <p className="text-gray-500 text-xs leading-relaxed mb-8">
                    Revisa tu bandeja de entrada y la carpeta de spam. Consulta
                    la fecha de vencimiento en el correo. Las solicitudes recientes
                    se agrupan para evitar mensajes duplicados.
                  </p>

                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        if (cooldown > 0) return;
                        setSent(false);
                        setError("");
                      }}
                      disabled={cooldown > 0}
                      className="w-full min-h-11 py-3 text-sm text-gray-500 hover:text-blue-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cooldown > 0
                        ? `Puedes reenviar en ${cooldown}s`
                        : "¿No recibiste el correo? Reenviar"}
                    </button>
                    <button
                      onClick={onBackToLogin}
                      className="w-full min-h-11 py-3 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-blue-900 transition-colors"
                    >
                      <ArrowLeft size={16} />
                      Volver a iniciar sesión
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
